import { DraftReferenceState } from "../../api/referenceOptions";
import { constructEarthRangerUrl, isEarthRangerDestinationType } from "../destinations/earthRangerUrl";

type ActionLike = { id: string; value: string; type: string; name?: string; schema?: any };

type BuildDraftReferenceArgs = {
    /** The type record (ProviderType or DestinationType). Uses .value as the runner-side key. */
    type: { value?: string; actions?: ActionLike[]; name?: string } | null | undefined;
    /** Selected workspace UUID. */
    owner: string | undefined;
    /** Raw value from the parent state. For EarthRanger providers/destinations this may be a site slug — the helper expands it. */
    baseUrl?: string | undefined;
    /** The rjsf configs keyed by IntegrationAction.id, as they live in the wizard state. */
    updatedConfigurations: Array<{ action: string; data?: Record<string, unknown> }>;
};

/** Assembles the draft-reference payload the widget and Test Connection button
 *  send to the ephemeral endpoint. Centralises three otherwise-easy-to-forget rules:
 *  - id → value + type resolution against the type's action definitions (rjsf keys
 *    configs by action.id but the runner takes action_value).
 *  - EarthRanger site slug → full URL expansion, so draft calls don't 502.
 *  - `data ?? {}` fallback so a section rjsf initialised without any onChange
 *    still ships an empty dict instead of dropping the key via JSON.stringify.
 */
export function buildDraftReference(
    { type, owner, baseUrl, updatedConfigurations }: BuildDraftReferenceArgs,
): DraftReferenceState | undefined {
    if (!type?.value || !owner) return undefined;
    const actionById = new Map<string, { value: string; type: string }>(
        (type.actions ?? []).map((a) => [a.id, { value: a.value, type: a.type }]),
    );
    const expandedBaseUrl = baseUrl && isEarthRangerDestinationType(type.name || "")
        ? constructEarthRangerUrl(baseUrl)
        : (baseUrl || undefined);
    const authAction = (type.actions ?? []).find((a) => a.type === "auth");
    return {
        typeId: type.value,
        owner,
        baseUrl: expandedBaseUrl,
        authValue: authAction?.value,
        authSchema: authAction?.schema,
        configurations: updatedConfigurations
            .map((c) => {
                const meta = actionById.get(c.action);
                return meta ? { actionValue: meta.value, actionType: meta.type, data: c.data ?? {} } : null;
            })
            .filter((c): c is { actionValue: string; actionType: string; data: Record<string, unknown> } => c !== null),
    };
}
